import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ActionIcon, Card, Loader, Select, Text } from "@mantine/core";
import { IconX, IconLayoutGrid, IconListCheck } from "@tabler/icons-react";
import { ConfirmationPlanogramViewer } from "./ConfirmationPlanogramViewer";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  mvpApiGetPlanogramProductOptions,
  mvpApiListStorePlanogramsOptions,
} from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import { useAppDispatch } from "@/store/hooks";
import { useActivePlanoProductInfoStore } from "@labeling-tools/ui-components/src/planogram-viewer/use-active-plano-product-info-store";
import {
  JobTaskDetailSchema,
  OoSParcelSchema,
} from "@augmodo/lebron-browser-client";
import { useTaskPlanogram } from "@/hooks/useTaskPlanogram";
import { useAtlasStores } from "@labeling-tools/api/hooks/atlas/useAtlasStores";
import {
  setSelectedProductId,
  setSelectedProductIds,
  setProductMatchConfirmed,
  setPlanogramOverrideId,
} from "@/store";
import { QaQuestionAction } from "../QaQuestionAction";
import { ViewMode } from "@/types";
import { ActionStatusBadge } from "../ActionStatusBadge";
import { isEmpty } from "lodash";
import { formatMoney } from "@labeling-tools/ui-components/src/utils/helpers";

interface SelectedProduct {
  product_id: string;
  planogram_product_id: string;
}

export const PlanogramOnlyWorkflow = ({
  capturedImage,
  task,
  parcel,
  mode,
}: {
  capturedImage: React.ReactNode;
  task: JobTaskDetailSchema;
  planogramSelectedProductId: string | null;
  parcel: OoSParcelSchema;
  mode?: ViewMode;
}) => {
  const dispatch = useAppDispatch();
  const [selectedPlanogramId, setSelectedPlanogramId] = useState<string | null>(
    null,
  );

  const isBoxDrawTask = !!(task.data as any)?.box_draw_task_id;

  // Multi-select state for box-draw tasks
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    [],
  );

  const { planogramData, isPlanogramLoading, aisleIndex } = useTaskPlanogram(
    task,
    parcel,
  );

  // Compute product IDs on the relevant aisle for zoom-to-aisle
  // For box-draw tasks, read from task data (self-contained); otherwise from parcel
  const taskData = task.data as any;
  const aisleProductIds = useMemo(() => {
    if (aisleIndex == null) return [];
    const productToAisle = isBoxDrawTask
      ? taskData?.product_to_aisle
      : parcel.query?.product_to_aisle;
    const productIds = isBoxDrawTask
      ? taskData?.product_ids
      : parcel.query?.product_ids;
    if (!productToAisle?.length || !productIds?.length) return [];

    const ids: string[] = [];
    for (let i = 0; i < productToAisle.length; i++) {
      if (productToAisle[i] === aisleIndex && productIds[i]) {
        ids.push(productIds[i]);
      }
    }
    return ids;
  }, [
    aisleIndex,
    isBoxDrawTask,
    taskData,
    parcel.query?.product_to_aisle,
    parcel.query?.product_ids,
  ]);

  // Fetch available planograms for the store so user can override auto-detected one
  // First resolve the internal store ID (e.g. "S_116") to an Atlas UUID
  const lebronStoreId =
    (task.data as any)?.internal_store_id ?? parcel?.store?.id;
  const { data: atlasStores } = useAtlasStores(
    isBoxDrawTask ? lebronStoreId : null,
  );
  const atlasStoreId = atlasStores?.stores?.[0]?.id;
  const { data: storePlanogramsData, isLoading: storePlanogramsLoading } =
    useQuery({
      ...mvpApiListStorePlanogramsOptions({
        query: {
          store_id: atlasStoreId,
          active: true,
        },
      }),
      enabled: !!atlasStoreId,
    });

  // The active planogram ID: user selection overrides auto-detected
  const activePlanogramId = selectedPlanogramId ?? planogramData?.id;

  // zustand state vars
  // the planogram-viewer component uses zustand to store the active label box info
  const activePlanogramProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.product_id,
  );
  const planogramProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.planogram_product_id,
  );
  const resetZustand = useActivePlanoProductInfoStore((s) => s.reset);

  // Single-select: fetch product details for the latest click (non-box-draw tasks)
  const { data: selectedProduct, isLoading: selectedProductLoading } = useQuery(
    {
      ...mvpApiGetPlanogramProductOptions({
        path: {
          planogram_id: activePlanogramId || "",
          planogram_product_id: planogramProductId || "",
        },
      }),
      enabled:
        !isBoxDrawTask && !!activePlanogramId && !!activePlanogramProductId,
    },
  );

  // Multi-select: fetch product details for all selected products
  const selectedProductQueries = useQueries({
    queries: isBoxDrawTask
      ? selectedProducts.map((sp) => ({
          ...mvpApiGetPlanogramProductOptions({
            path: {
              planogram_id: activePlanogramId || "",
              planogram_product_id: sp.planogram_product_id,
            },
          }),
          enabled: !!activePlanogramId,
        }))
      : [],
  });

  const getInitialProductId = () => {
    // admin view
    if (!isEmpty(task?.result)) {
      return task?.result?.confirmation_product_id || null;
    }
    // qa view
    if (task?.data?.confirmation_result) {
      return (
        (task?.data?.confirmation_result as any)?.confirmation_product_id ||
        null
      );
    }
    // OOS confirmation view
    if (task?.data?.identified_product_id) {
      return task?.data?.identified_product_id;
    }
    return null;
  };

  // Track previous activePlanogramProductId to detect changes
  const prevProductIdRef = useRef<string>("");

  // Box-draw multi-select: accumulate selections from zustand clicks
  useEffect(() => {
    if (!isBoxDrawTask) {
      // Single-select for non-box-draw tasks (original behavior)
      if (activePlanogramProductId) {
        dispatch(setSelectedProductId(activePlanogramProductId));
        dispatch(setProductMatchConfirmed(true));
      } else {
        dispatch(setProductMatchConfirmed(null));
        dispatch(setSelectedProductId(null));
      }
      return;
    }

    // Multi-select for box-draw tasks
    if (
      activePlanogramProductId &&
      activePlanogramProductId !== prevProductIdRef.current
    ) {
      prevProductIdRef.current = activePlanogramProductId;

      setSelectedProducts((prev) => {
        const exists = prev.some(
          (p) => p.product_id === activePlanogramProductId,
        );
        if (exists) {
          return prev.filter((p) => p.product_id !== activePlanogramProductId);
        } else {
          return [
            ...prev,
            {
              product_id: activePlanogramProductId,
              planogram_product_id: planogramProductId,
            },
          ];
        }
      });
    } else if (!activePlanogramProductId && prevProductIdRef.current) {
      // User clicked the active box again to deselect — zustand reset
      // product_id to "". Remove the previously active product from selections.
      const prevId = prevProductIdRef.current;
      prevProductIdRef.current = "";

      setSelectedProducts((prev) =>
        prev.filter((p) => p.product_id !== prevId),
      );
    }
  }, [activePlanogramProductId, planogramProductId, isBoxDrawTask]);

  // Sync selectedProducts to Redux in a separate effect
  useEffect(() => {
    if (!isBoxDrawTask) return;
    dispatch(setSelectedProductIds(selectedProducts.map((p) => p.product_id)));
    dispatch(
      setSelectedProductId(
        selectedProducts.length > 0
          ? selectedProducts[selectedProducts.length - 1].product_id
          : null,
      ),
    );
    dispatch(
      setProductMatchConfirmed(selectedProducts.length > 0 ? true : null),
    );
  }, [selectedProducts, isBoxDrawTask, dispatch]);

  const handleRemoveProduct = useCallback(
    (productId: string) => {
      setSelectedProducts((prev) => {
        const next = prev.filter((p) => p.product_id !== productId);
        dispatch(setSelectedProductIds(next.map((p) => p.product_id)));
        dispatch(
          setSelectedProductId(
            next.length > 0 ? next[next.length - 1].product_id : null,
          ),
        );
        dispatch(setProductMatchConfirmed(next.length > 0 ? true : null));
        return next;
      });
    },
    [dispatch],
  );

  // Clear selections when the active planogram changes
  useEffect(() => {
    resetZustand();
    setSelectedProducts([]);
    prevProductIdRef.current = "";
    dispatch(setSelectedProductIds([]));
    dispatch(setSelectedProductId(null));
    dispatch(setProductMatchConfirmed(null));
  }, [activePlanogramId, resetZustand, dispatch]);

  // Reset state when task changes or component unmounts
  useEffect(() => {
    resetZustand();
    setSelectedPlanogramId(null);
    dispatch(setPlanogramOverrideId(null));
    setSelectedProducts([]);
    prevProductIdRef.current = "";

    return () => {
      resetZustand();
    };
  }, [task?.id, resetZustand]);

  return (
    <div className="flex gap-6">
      {/* Left column: captured image */}
      <div className="w-1/3">{capturedImage}</div>

      {/* Right column: Product Match */}
      <div className="w-2/3">
        <Card shadow="lg" padding="lg" radius="md" withBorder>
          <Text size="md" fw={700} className="mb-1 text-left">
            Product Match
          </Text>
          <Text size="sm" c="dimmed" className="mb-4 text-left">
            Use the planogram to determine which item should be in the empty
            area in the reference image (Red Box)
          </Text>

          {/* Planogram dropdown for box-draw tasks */}
          {isBoxDrawTask && (
            <div className="mb-4 text-left">
              {storePlanogramsLoading || isPlanogramLoading ? (
                <div>
                  <div className="flex items-center gap-1.5 mb-1 text-left">
                    <IconLayoutGrid size={16} />
                    <Text size="sm" fw={500}>
                      Select a planogram
                    </Text>
                  </div>
                  <div className="h-[42px] rounded border border-gray-300 bg-gray-100 flex items-center justify-center">
                    <Loader size="xs" />
                  </div>
                </div>
              ) : storePlanogramsData?.store_planograms?.length ? (
                <Select
                  label={
                    <div className="flex items-center gap-1.5 text-left">
                      <IconLayoutGrid size={16} />
                      <span>Select a planogram</span>
                    </div>
                  }
                  placeholder="Choose a planogram..."
                  value={activePlanogramId}
                  onChange={(value) => {
                    setSelectedPlanogramId(value);
                    dispatch(setPlanogramOverrideId(value));
                  }}
                  data={storePlanogramsData.store_planograms.map((sp) => ({
                    value: sp.planogram.id || "",
                    label: sp.planogram.description || sp.planogram.id || "",
                  }))}
                  size="md"
                  searchable
                />
              ) : (
                <Text>No planograms available for this store</Text>
              )}
            </div>
          )}

          {/* Planogram viewer */}
          {activePlanogramId && (
            <ConfirmationPlanogramViewer
              planogramId={activePlanogramId}
              initialProductId={getInitialProductId()}
              selectedProductIds={
                isBoxDrawTask
                  ? selectedProducts.map((p) => p.product_id)
                  : undefined
              }
              zoomToProductIds={
                !getInitialProductId() && aisleProductIds.length > 0
                  ? aisleProductIds
                  : undefined
              }
            />
          )}

          {/* No planogram found - non-box-draw tasks only */}
          {!isBoxDrawTask && !planogramData?.id && !isPlanogramLoading && (
            <div>
              <Text>No planogram found</Text>
            </div>
          )}

          {/* Loading state */}
          {isPlanogramLoading && (
            <div
              style={{ height: 400 }}
              className="flex items-center justify-center"
            >
              <Loader />
            </div>
          )}

          {/* Selected Items */}
          <div className="mt-4">
            <div className="flex items-center gap-1.5 mb-2 text-left">
              <IconListCheck size={16} />
              <Text size="sm" fw={600}>
                {isBoxDrawTask ? "Selected Items" : "Current Selected Item"}
              </Text>
            </div>

            {isBoxDrawTask ? (
              // Multi-select display for box-draw tasks
              selectedProducts.length > 0 ? (
                <div className="space-y-3">
                  {selectedProducts.map((sp, index) => {
                    const query = selectedProductQueries[index];
                    const product = query?.data;
                    const loading = query?.isLoading;

                    return (
                      <div
                        key={sp.product_id}
                        className="flex gap-4 items-start border rounded p-3"
                      >
                        <div className="size-20 flex-shrink-0 bg-white border rounded">
                          {loading ? (
                            <div className="flex items-center justify-center size-full">
                              <Loader size="xs" />
                            </div>
                          ) : (
                            !!product?.image_url && (
                              <img
                                src={product.image_url}
                                className="size-full object-contain"
                                alt={product.description || "Product"}
                              />
                            )
                          )}
                        </div>
                        <div className="flex-1 text-sm space-y-1">
                          {!!product?.description && (
                            <div>{product.description}</div>
                          )}
                          {!!(product as any)?.upc && (
                            <div>
                              <span className="font-semibold">upc:</span>{" "}
                              {(product as any).upc}
                            </div>
                          )}
                          <div>
                            <span className="font-semibold">facings:</span>{" "}
                            {product?.facings ?? 0}
                          </div>
                          {typeof product?.price === "number" && (
                            <div>
                              <span className="font-semibold">price:</span>{" "}
                              {formatMoney(product?.price)}
                            </div>
                          )}
                          {!!(product as any)?.supplier && (
                            <div>
                              <span className="font-semibold">supplier:</span>{" "}
                              {(product as any).supplier}
                            </div>
                          )}
                        </div>
                        {mode === "viewer" && (
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => handleRemoveProduct(sp.product_id)}
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Text size="sm" c="dimmed" className="text-left">
                  Click products in the planogram to select them
                </Text>
              )
            ) : // Single-select display for non-box-draw tasks
            selectedProduct ? (
              <div className="flex gap-4 items-start">
                <div className="size-24 flex-shrink-0 bg-white border rounded">
                  {!!selectedProduct?.image_url && (
                    <img
                      src={selectedProduct.image_url}
                      className="size-full object-contain"
                      alt={selectedProduct.description || "Product"}
                    />
                  )}
                </div>
                <div className="text-sm space-y-1">
                  {!!selectedProduct?.description && (
                    <div>{selectedProduct.description}</div>
                  )}
                  {!!(selectedProduct as any)?.upc && (
                    <div>
                      <span className="font-semibold">upc:</span>{" "}
                      {(selectedProduct as any).upc}
                    </div>
                  )}
                  <div>
                    <span className="font-semibold">facings:</span>{" "}
                    {selectedProduct?.facings ?? 0}
                  </div>
                  {typeof selectedProduct?.price === "number" && (
                    <div>
                      <span className="font-semibold">price:</span>{" "}
                      {formatMoney(selectedProduct?.price)}
                    </div>
                  )}
                  {!!(selectedProduct as any)?.supplier && (
                    <div>
                      <span className="font-semibold">supplier:</span>{" "}
                      {(selectedProduct as any).supplier}
                    </div>
                  )}
                </div>
              </div>
            ) : selectedProductLoading ? (
              <div className="flex items-center h-24">
                <Loader size="sm" />
              </div>
            ) : (
              <Text size="sm" c="dimmed" className="text-left">
                Click a product in the planogram to select it
              </Text>
            )}
          </div>

          {/* Admin status badge */}
          {mode === "admin" && (
            <div className="mt-4">
              <ActionStatusBadge
                task={task}
                resultField="product_match_confirmed"
              />
            </div>
          )}

          {/* QA related statuses */}
          {(mode === ViewMode.CONFIRMATION_QA ||
            mode === ViewMode.CONFIRMATION_QA_FEEDBACK) && (
            <div className="mt-4">
              <ActionStatusBadge
                task={{
                  ...task,
                  result: task.data?.confirmation_result || ({} as any),
                }}
                resultField="product_match_confirmed"
              />
              <QaQuestionAction questionNumber={3} task={task} />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
